import { _ as __rest } from './_tslib.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { generateClasses } from '@ids/tools';
import { useRef, useContext, useMemo, useEffect } from 'react';
import { I as IdsFormSelection } from './form-selection.js';
import { s as styleInject } from './style-inject.es.js';
import { u as useId } from './use-id.js';
import { F as FormSelectionContext } from './form-selection.context.js';

var css_248z = ".ids-checkbox {\n  -webkit-box-align: center;\n      -ms-flex-align: center;\n          align-items: center;\n  color: var(--ids_color_action_neutral_base);\n  display: -webkit-inline-box;\n  display: -ms-inline-flexbox;\n  display: inline-flex;\n  font-size: 0;\n  -webkit-box-pack: center;\n      -ms-flex-pack: center;\n          justify-content: center;\n  line-height: 0;\n  min-height: var(--ids_size_general_48x);\n  min-width: var(--ids_size_general_48x);\n  position: relative;\n}\n.ids-checkbox__input {\n  -webkit-appearance: none;\n     -moz-appearance: none;\n          appearance: none;\n  background-color: transparent;\n  border-radius: 0;\n  cursor: pointer;\n  height: 100%;\n  left: 0;\n  position: absolute;\n  top: 0;\n  width: 100%;\n}\n.ids-checkbox__input:disabled {\n  cursor: not-allowed;\n}\n.ids-checkbox__icon::before {\n  content: \"checkbox_vazio\";\n}\n.ids-checkbox__input:indeterminate + .ids-checkbox__icon::before {\n  content: \"checkbox_indeterminado\";\n}\n.ids-checkbox--invalid .ids-checkbox__icon {\n  color: var(--ids_color_feedback_error);\n}\n.ids-checkbox__input[aria-invalid=true] + .ids-checkbox__icon {\n  color: var(--ids_color_feedback_error);\n}\n.ids-checkbox__input:disabled + .ids-checkbox__icon {\n  color: var(--ids_color_action_disabled_variant);\n}\n.ids-checkbox__input:checked + .ids-checkbox__icon::before {\n  content: \"checkbox\";\n}\n.ids-checkbox__input:not(:disabled):hover + .ids-checkbox__icon {\n  color: var(--ids_color_action_neutral_variant);\n}\n.ids-checkbox__input:indeterminate:not(:disabled) + .ids-checkbox__icon, .ids-checkbox__input:checked:not(:disabled) + .ids-checkbox__icon {\n  color: var(--ids_color_action_primary_base);\n}\n.ids-checkbox__input:indeterminate:not(:disabled):hover + .ids-checkbox__icon, .ids-checkbox__input:checked:not(:disabled):hover + .ids-checkbox__icon {\n  color: var(--ids_color_action_primary_variant);\n}";
styleInject(css_248z);

/**
 * @category Component
 *
 * @example
 * ```ts
 * import { IdsCheckbox } from '@ids/react';
 * ```
 */
const IdsCheckbox = _a => {
  var {
      id: externalId,
      className = '',
      children,
      label,
      formSelectionProps,
      invalid = false,
      contrast,
      indeterminate,
      'aria-labelledby': externalAriaLabelledBy
    } = _a,
    props = __rest(_a, ["id", "className", "children", "label", "formSelectionProps", "invalid", "contrast", "indeterminate", 'aria-labelledby']);
  const {
    checked
  } = props;
  const id = useId(externalId, 'ids-react-checkbox');
  const inputRef = useRef();
  const {
    setInput,
    labelIds: labelIds
  } = useContext(FormSelectionContext);
  const ariaLabelledBy = externalAriaLabelledBy || (labelIds.length ? labelIds.join(' ') : undefined);
  const classes = generateClasses({
    'ids-checkbox': true,
    'ids-checkbox--invalid': invalid,
    [className]: className
  });
  const memoizedInput = useMemo(() => jsxs("span", Object.assign({
    className: classes
  }, {
    children: [jsx("input", Object.assign({
      className: "ids-checkbox__input",
      type: "checkbox",
      id: id,
      "aria-labelledby": ariaLabelledBy,
      "aria-checked": checked
    }, props, {
      ref: inputRef
    })), jsx("i", {
      "aria-hidden": true,
      className: "ids-checkbox__icon ids-icon ids-icon--medium"
    })]
  })), [classes, id, ariaLabelledBy, checked, props]);
  useEffect(() => {
    if (indeterminate) {
      inputRef.current.indeterminate = true;
    }
  }, [indeterminate]);
  useEffect(() => {
    if (setInput) {
      setInput(inputRef.current);
    }
  }, [id, setInput]);
  if (children || label) {
    return jsx(IdsFormSelection, Object.assign({
      contrast: contrast,
      input: memoizedInput,
      disabled: props.disabled,
      inputId: id
    }, formSelectionProps, {
      children: children || label
    }));
  }
  return memoizedInput;
};

export { IdsCheckbox as I };
//# sourceMappingURL=checkbox.js.map
